<template>
  <div id="counselors" ref="counselors" class="group group--sidebar group--df-column">
    <h1 
      :style="{ 'color': org.custom_color1 == null ? '': org.custom_color1 }"
      class="h1 group__title mw800">We've found some counselors for you in <span>{{ getState }}</span></h1>

    <div class="results__header">
      <div v-if="!openSearch" class="results__search" @click="openSearch = true">
        <module-icon icon="search" />
        <span 
          :style="{ 'color': org.custom_color1 == null ? '': org.custom_color1 }"
          class="link fw-medium c-accent">Search by name</span>
      </div>
      <p class="p c-mid">
        <span 
          @click="showStateModal = true" 
          :style="{ 'color': org.custom_color1 == null ? '': org.custom_color1 }"
          class="link c-accent">Change your state</span>
        <br class="hide-above-medium">
        <span class="ml hide-below-medium" />
        <span 
          v-if="!org.dfd_client"
          :style="{ 'color': org.custom_color1 == null ? '': org.custom_color1 }"
          class="link fw-medium c-accent" 
          @click="navigateToF2f">{{ handlingF2fRedirect ? `Redirecting...` : `We also offer in-person sessions.` }}</span>
      </p>
    </div>

    <module-modal
      :modal="modalStateData"
      @close="showStateModal = false"
      v-show="showStateModal"
    >

      <template slot="modal-body">
        <div class="form results__state">
          <div class="form-wrap">
            <div class="form-fieldset">
              <!-- <label class="form-label">Your state</label> -->
              <div class="form-field">
                <div class="form-wrap">
                  <select
                    class="form-select"
                    @change="handleStateSelect($event)"
                  >
                    <option
                      v-for="choice in states"
                      :value="choice.value"
                      :key="choice.value"
                      :selected="choice.label === getState"
                      :disabled="choice.label === getState"
                    >
                      {{ choice.label }}
                    </option>
                  </select>
                  <span class="form-arrow" />
                </div>
              </div>
            </div>
          </div>
        </div>
      </template>

      <template slot="modal-footer">
        <module-button @click.native="updateState">Update</module-button>
        <module-button @click.native="showStateModal = false">Cancel</module-button>
      </template>
    </module-modal>


    <transition name="intake" mode="out-in">
      <div class="form-fieldset" v-if="openSearch">
        <div class="form-fieldset__wrapper">
          <label for="search" :class="['form-label', { 'form-label--focus' : search.length > 0 }]">
            <span class="form-label__label p">Search by first or last name</span>
          </label>
          <div :class="['form-field', { 'form-field--focus' : search.length > 0 }]">
            <input
              v-model="search"
              id="search"
              placeholder="Search by first or last name"
              type="text"
              class="form-input form-input--text"
            >
          </div>
        </div>
      </div>
    </transition>

    <div v-if="openSearch && allProviders.length <= 0" class="results__empty">
      <p class="p c-error">
        Sorry, no results found for {{ search }}. Try again or use "Load More" to find more counselors available to you.
      </p>
    </div>

    <transition name="intake" mode="out-in">
      <div v-if="loadingListOfProviders" class="list-provider__loader">
        <module-dots :active="true" />
      </div>

      <ul
        v-if="!loadingListOfProviders"
        :class="[
          'list-provider',
          {
            'list-provider--search-open': openSearch
          }
        ]"
      >
        <li
          v-for="(provider, index) in allProviders"
          :key="provider.id"
          @click="select(provider)"
          class="list-provider__item"
        >
          <div class="list-provider__avatar" :style="formatAvatar(provider.photo_url_absolute)"/>

          <div class="list-provider__content">
            <div class="list-provider__provider par">
              <span 
                v-if="index === 0" 
                :style="{ 'background-color': org.custom_color1 == null ? '': org.custom_color1 }"
                class="list-provider__pill">Top match</span>
              <h2 
                :style="{ 'color': org.custom_color1 == null ? '': org.custom_color1 }"
                class="h2 list-provider__title">{{ provider.fullname }}</h2>
              <span class="fw-bold c-mid">{{ provider.specialty }}</span>
            </div>

            <div class="list-provider__availability">
              <span v-if="provider.next_appt_available_date" class="c-mid">Available {{ provider.next_appt_available_date | readableDateTime }}</span>

              <p 
                :style="{ 'color': org.custom_color1 == null ? '': org.custom_color1 }"
                class="list-provider__link fw-bold">Find a time to talk</p>
            </div>
          </div>
        </li>
      </ul>
    </transition>

    <div v-if="!loadingListOfProviders" class="results__pagination">
      <module-button
        class="mt"
        :label="!noResults ? `Load More` : `All results loaded`"
        button="primary"
        @click.native="nextResults"
        :disabled="noResults"
      />
    </div>

    <module-disclaimer class="disclaimer--results" />

    <article class="bbg bbg--white group group--wide">
      <section id="approach" ref="approach" class="section section--approach ovh">
        <h1 
          :style="{ 'color': org.custom_color1 == null ? '': org.custom_color1 }"
          class="h1">Get the right help from the right person</h1>

        <p class="p">There’s no one-size-fits-all solution to feeling better. So our network includes counselors who specialize in different areas of emotional health.</p>
        <p class="p">Question about your results? Want to talk to someone in person? Please give us a call at <a 
          :style="{ 'color': org.custom_color1 == null ? '': org.custom_color1 }"
          class="fw-bold c-accent tdn link"
          :href="`tel:${number}`">{{ number }}</a>.</p>
      </section>
      <section id="things-to-know" class="section section--things-to-know">
        <h1 
          :style="{ 'color': org.custom_color1 == null ? '': org.custom_color1 }"
          class="h1 mb0">Some other things to know<br class="hide-below-medium"> about how this works</h1>

        <div class="section__group">
          <div class="section__group-item">
            <h2 
              :style="{ 'color': org.custom_color1 == null ? '': org.custom_color1 }"
              class="h2">It's already paid for</h2>
            <p class="p">Your employer covers the cost for you and your family members. That means no bills, no co-pays, no surprise fees, and no need to involve your insurance company.</p>

            <h2 
              :style="{ 'color': org.custom_color1 == null ? '': org.custom_color1 }"
              class="h2">It’s confidential</h2>
            <p class="p">Your conversations are private, so your employer won’t know who you talked to or what you said.</p>
          </div>

          <div class="section__group-item">
            <h2 
              :style="{ 'color': org.custom_color1 == null ? '': org.custom_color1 }"
              class="h2">It’s based around you</h2>
            <p class="p">You’ll choose how you want to talk: over the phone or using video chat. You’ll get personalized, one-on-one help. And if you ever need anything, you’ll always be able to call us.</p>

            <h2 
              :style="{ 'color': org.custom_color1 == null ? '': org.custom_color1 }"
              class="h2">It’s quality experts only</h2>
            <p class="p">You’ll be connected with someone who’s been vetted, screened, and approved by our team.</p>
          </div>
        </div>
      </section>
    </article>
  </div>
</template>

<script>
// Vuex
import { mapGetters, mapActions } from 'vuex';

// 3rd party
import moment from 'moment';
import _ from 'lodash';

// Modules
import ModuleDisclaimer from 'components/Disclaimer';
import ModuleDots from 'components/Dots';
import ModuleIcon from 'components/Icon';
import ModuleModal from 'components/Modal';

// Mixins
import MixinIntakeProgress from 'components/IntakeProgress';

// Utils
import { handleF2fSelection, getF2fStartingQuestion } from 'utils/f2fSelection';

// Models
import states from 'models/contact/states';

import illustration from 'images/results/results-illustration.png';

const images = {
  illustration,
};

// Export
export default {
  name: 'view-register',


  // Components
  components: {
    ModuleDisclaimer,
    ModuleDots,
    ModuleIcon,
    ModuleModal,
  },

  // Filters
  filters: {
    readableDateTime (date) {
      if ( !date ) return;

      return moment(date).calendar(null, {
        sameDay: '[Today] [at] h:mm a',
        nextDay: '[Tomorrow] [at] h:mm a',
        nextWeek: 'dddd, MMM DD',
        lastDay: '[Yesterday]',
        lastWeek: '[Last] dddd',
        sameElse: 'dddd, MMM DD',
      });
    },
  },

  // Mixins
  mixins: [
    MixinIntakeProgress,
  ],

  // Data
  data() {
    return {
      image: images.illustration,
      handlingF2fRedirect: false,
      results: this.providers || [],
      pageNum: 1,
      openSearch: false,
      search: '',
      states,
      modalStateData: {
        title: 'Which state will you be in during your appointment?',
      },
      showStateModal: false,
      savedState: null,
      tempState: null,
    };
  },

  // Computed
  computed: {
    ...mapGetters('provider', ['loadingListOfProviders', 'providers', 'noResults']),
    ...mapGetters('answers', ['allQuestions']),
    ...mapGetters('app', ['org']),
    ...mapGetters('auth', ['isAuthenticated']),
    ...mapGetters('auth', ['user']),

    illustration() {
      if ( !this.image ) return;

      return { backgroundImage: `url(${this.image})` };
    },

    number() {
      return this.org.phone;
    },

    userData() {
      return JSON.parse(localStorage.getItem('updateUser'));
    },

    getState() {
      let userState = '';

      if (localStorage.getItem('answerLocation') && this.savedState <= 0) {
        return userState = localStorage.getItem('answerLocation');
      }

      userState = this.savedState || this.userData.appointment_state;

      return states.filter((state) => state.value === userState)[0].label;
    },

    answers() {
      if(localStorage.getItem('answerComplaint')) {
        return `${localStorage.getItem('answerComplaint')} ${localStorage.getItem('answerSecondaryComplaint') ? `and ${localStorage.getItem('answerSecondaryComplaint')}` : ''}`;
      }
      else if (localStorage.getItem('updateUser')) {
        return `${this.userData.chief_complaint1} ${this.userData.chief_complaint2 ? `and ${this.userData.chief_complaint2}` : ''}`;
      }
    },

    allProviders() {
      if (this.search) {
        return this.providers.filter(person => person.fullname.toLowerCase().includes(this.search) || person.fullname.includes(this.search));
      }

      return this.providers;
    },
  },

  // Methods
  methods: {
    ...mapActions('provider', ['getListOfProviders', 'saveSelectedProvider']),
    ...mapActions('sidebar', ['saveActiveSection']),
    ...mapActions('auth', ['requestUpdateState', 'requestCurrentProfile']),

    formatAvatar(url) {
      if ( url ) { return { backgroundImage: `url(${url})` }; }
    },

    select(provider) {
      const selectedProvider = {
        topMatch: _.findIndex(this.providers, p => p.id === provider.id) === 0, // eslint-disable-line no-undef
        ...provider,
      };

      this.saveSelectedProvider(selectedProvider);
      localStorage.setItem('selectedProvider', selectedProvider);
      this.$router.push({
        name: 'provider-schedule',
      });
    },

    handleScroll() {
      const approach = this.$refs.approach.getBoundingClientRect();

      if ( approach.top > 0 ) {
        this.saveActiveSection('counselors');
      }

      if ( approach.top <= 10 && approach.top >= -approach.height ) {
        this.saveActiveSection('approach');
      }

      if ( approach.top < -approach.height ) {
        this.saveActiveSection('thingsToKnow');
      }
    },

    navigateToF2f() {
      this.handlingF2fRedirect = true;
      handleF2fSelection()
        .then(() => {
          this.$router.push({
            name: 'face-2-face', params: {
              id: getF2fStartingQuestion(),
            },
          });
        });
    },

    handleStateSelect(e) {
      this.tempState = e.target.value;
    },

    changeState() {
      this.showStateModal = false;
      this.getListOfProviders({ page: this.pageNum = 1, userState: this.tempState });
      this.savedState = this.tempState;
      localStorage.setItem('tempState', this.savedState);
    },

    updateState() {
      const state = states.filter((s => s.value === this.tempState))[0];

      if (this.isAuthenticated) {
        this.requestUpdateState(state.label)
          .then(() => {
            this.requestCurrentProfile();
            this.changeState();
          })
          .catch(err => console.error(err));
      } else {
        this.changeState();
      }
    },

    nextResults() {
      if (this.tempState) {
        this.getListOfProviders({ page: this.pageNum += 1, userState: this.tempState });
      } else {
        this.getListOfProviders({ page: this.pageNum += 1 });
      }
    },
  },

  // Lifecycles
  created() {
    if ( !this.isAuthenticated ) {
      if (
        !this.checkIntakeRequirements('default', 'intakeDetail', 2) &&
        !this.checkIntakeRequirements('default', null, null)
      ) {
        this.$router.push({ name: 'home' });

        return;
      }

      this.checkIntakeRequirements('default', 'intakeDetail', 2, 'help');
    }

    if (JSON.parse(localStorage.getItem('f2fselected')) === true) {
      this.$router.push({ name: 'appointment-type' });
      localStorage.setItem('f2fselected', false);
    }

    this.getListOfProviders({ page: this.pageNum });

    window.addEventListener('scroll', this.handleScroll);

    if (localStorage.getItem('tempState')) localStorage.removeItem('tempState');
  },

  beforeRouteEnter (to, from, next) {
    if ( !from.name ) {
      next(vm => {
        if ( !vm.checkIntakeRequirements('default', null, null) ) {
          next({ name: 'home' });
        } else {
          vm.redirectTo('help');
        }
      });
    }

    next();
  },

  destroyed () {
    window.removeEventListener('scroll', this.handleScroll);
  },

  beforeRouteLeave(to, from, next) {
    this.$store.commit('provider/SAVE_LIST_OF_PROVIDERS', []);
    this.$store.commit('provider/HAS_RESULTS', false);
    next();
  },
};
</script>



// WEBPACK FOOTER //
// Results.vue?2195c177